import { supabase } from '../lib/supabase';

export interface ContentVideo {
  id: string;
  content_id: string; // referencia a content_items.id
  title: string;
  description: string | null;
  youtube_url: string;
  youtube_video_id: string;
  thumbnail_url: string;
  order_index: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

export function extractYouTubeVideoId(url: string): string | null {
  if (!url) return null;
  try {
    // Try parsing standard v parameter first
    try {
      const parsed = new URL(url);
      const v = parsed.searchParams.get('v');
      if (v && v.length === 11) return v;
    } catch (_) {}

    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      return match[2];
    }
    
    const shortsRegExp = /youtube\.com\/shorts\/([\w-]{11})/i;
    const shortsMatch = url.match(shortsRegExp);
    if (shortsMatch) {
      return shortsMatch[1];
    }

    // Fallback: search for any 11-character token that looks like a YouTube ID
    const fallbackRegExp = /([\w-]{11})/;
    const fallbackMatch = url.match(fallbackRegExp);
    if (fallbackMatch) {
      return fallbackMatch[1];
    }

    return null;
  } catch {
    return null;
  }
}

export function buildYouTubeThumbnailUrl(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

export const loadVideos = async (contentId: string): Promise<ContentVideo[]> => {
  try {
    const { data, error } = await supabase
      .from('content_videos')
      .select('*')
      .eq('content_id', contentId)
      .order('order_index', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Erro ao carregar vídeos:', error);
    return [];
  }
};

export const createVideo = async (
  video: Pick<
    ContentVideo,
    'content_id' | 'title' | 'description' | 'youtube_url' | 'youtube_video_id' | 'thumbnail_url' | 'order_index' | 'is_published'
  >
): Promise<ContentVideo | null> => {
  try {
    const { data, error } = await supabase
      .from('content_videos')
      .insert(video)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erro ao criar vídeo:', error);
    return null;
  }
};

export const updateVideo = async (
  id: string,
  updates: Partial<ContentVideo>
): Promise<ContentVideo | null> => {
  try {
    const { data, error } = await supabase
      .from('content_videos')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erro ao atualizar vídeo:', error);
    return null;
  }
};

export const deleteVideo = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('content_videos')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Erro ao excluir vídeo:', error);
    return false;
  }
};


